import { TabBar } from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import { useEffect, useState } from 'react';
import type { EpisodeCategory } from '@common/enums';
import type { IOrganization } from '@frontend/components/interfaces';
import { getOneOrganization } from '@frontend/services/api/organizationsApiService';
import { getSelectedLanguage } from '@frontend/translate';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { isMedicalProtocolAvailable } from '@frontend/utils/medicalProtocolAvailability';

interface IEpisodeTabBarProps {
    episodeCategory: EpisodeCategory;
    organizationId: string;
    pathname: string;
    patientId: string;
}

type EpisodeTab = {
    key: 'episode' | 'contactsAndTasks' | 'medicalProtocol';
    path: string;
    translation: string;
};

export function EpisodeHeaderTabBar(props: IEpisodeTabBarProps) {
    const abortSignal = useAbortSignal();
    const navigate = useNavigate();
    const translate = getSelectedLanguage();
    const [organization, setOrganization] = useState<IOrganization | null>(null);
    const [authorizedTabs, setAuthorizedTabs] = useState<EpisodeTab[]>([]);

    useEffect(() => {
        loadOrganization();
    }, [props.organizationId]);

    useEffect(() => {
        if (organization) {
            loadTabs();
        }
    }, [organization, props.episodeCategory]);

    const episodeTabs: EpisodeTab[] = [
        {
            key: 'episode',
            path: '/episode',
            translation: translate.episodeCategory.episodeCategory,
        },
        {
            key: 'contactsAndTasks',
            path: '/contactsAndTasks',
            translation: translate.common.support,
        },
        {
            key: 'medicalProtocol',
            path: '/medicalProtocol',
            translation: translate.common.medicalProtocol,
        },
    ];

    function getUrlWithParams(url: string) {
        return `${url}?patientId=${props.patientId}&episodeCategory=${props.episodeCategory}`;
    }

    async function loadOrganization(): Promise<void> {
        const oneOrganization = await getOneOrganization(props.organizationId, abortSignal);
        setOrganization(oneOrganization);
    }

    async function isTabAccessible(key: EpisodeTab['key']): Promise<boolean> {
        switch (key) {
            case 'episode':
                return true;
            case 'contactsAndTasks':
                return organization?.areContactsAndTasksEnabled ?? false;
            case 'medicalProtocol':
                return isMedicalProtocolAvailable(props.episodeCategory);
            default:
                return false;
        }
    }

    async function loadTabs(): Promise<void> {
        const tabs: EpisodeTab[] = [];
        for (const tab of episodeTabs) {
            if (await isTabAccessible(tab.key)) {
                tabs.push(tab);
            }
        }
        setAuthorizedTabs(tabs);
    }

    if (!organization || authorizedTabs.length <= 1) {
        return null;
    }

    return (
        <TabBar>
            {authorizedTabs.map((tab) => (
                <TabBar.Button
                    isSelected={props.pathname === tab.path}
                    key={tab.key}
                    onClick={() => navigate(getUrlWithParams(tab.path))}
                >
                    {tab.translation}
                </TabBar.Button>
            ))}
        </TabBar>
    );
}
